import React, { useState, useEffect, useCallback } from 'react';
import { api, endpoints } from '../../config/api';
import { Card, Button, Alert, Spinner, Badge } from '../ui';
import { useT, apiErrorMessage } from '../../i18n';
import './GraphConnectionCard.css';

const FAILURE_REASONS = ['expired', 'declined', 'verification_failed', 'not_found'];

const messages = {
  expired: { tr: 'Bağlantı isteğinin süresi doldu. Lütfen tekrar deneyin.', en: 'The connection request expired. Please try again.' },
  declined: { tr: 'Microsoft yönetici onayı verilmedi ya da iptal edildi.', en: 'Microsoft admin consent was not granted or was cancelled.' },
  verification_failed: { tr: 'Onay alındı ama bağlantı doğrulanamadı. Lütfen birkaç dakika sonra tekrar deneyin.', en: 'Consent was received but the connection couldn’t be verified. Please try again in a few minutes.' },
  not_found: { tr: 'Organizasyon bulunamadı.', en: 'Organization not found.' },
  connectFailed: { tr: 'Bağlantı kurulamadı. Lütfen tekrar deneyin.', en: 'The connection couldn’t be established. Please try again.' },
  startFailed: { tr: 'Bağlantı başlatılamadı', en: 'The connection couldn’t be started' },
  confirmDisconnect: {
    tr: 'Microsoft 365 bağlantısını kaldırmak istediğinize emin misiniz? İmza dağıtımı bu bağlantı olmadan çalışmaz.',
    en: 'Are you sure you want to remove the Microsoft 365 connection? Signature distribution doesn’t work without it.',
  },
  disconnectFailed: { tr: 'Bağlantı kaldırılamadı', en: 'The connection couldn’t be removed' },
  title: { tr: 'Microsoft 365 Bağlantısı', en: 'Microsoft 365 Connection' },
  connected: { tr: 'Bağlı', en: 'Connected' },
  notConnected: { tr: 'Bağlı Değil', en: 'Not Connected' },
  yourTenant: { tr: 'Microsoft 365 tenant\'ınız', en: 'your Microsoft 365 tenant' },
  connectedDesc: {
    tr: ({ tenant, deployed }) => <><strong>{tenant}</strong> ile bağlantı kuruldu. Personel ve grup bilgileriniz senkronize ediliyor.{deployed ? ' İmzalar Outlook kullanıcılarınıza otomatik olarak dağıtılıyor.' : ' İmzaların Outlook\'ta otomatik görünmesi için aşağıdaki Add-in dağıtım adımını da tamamlamanız gerekir.'}</>,
    en: ({ tenant, deployed }) => <>Connected to <strong>{tenant}</strong>. Your people and group data is being synchronized.{deployed ? ' Signatures are being distributed to your Outlook users automatically.' : ' To have signatures appear in Outlook automatically, also complete the add-in deployment step below.'}</>,
  },
  connectedAt: { tr: 'Bağlantı tarihi: {date}', en: 'Connected on: {date}' },
  removing: { tr: 'Kaldırılıyor...', en: 'Removing...' },
  disconnect: { tr: 'Bağlantıyı Kaldır', en: 'Remove Connection' },
  notConnectedDesc: {
    tr: () => <>İmzaların Outlook kullanıcılarınıza otomatik dağıtılabilmesi için Microsoft 365 hesabınızı bağlamanız gerekir. Bu işlem, organizasyonunuzun <strong>Global Admin</strong> yetkisine sahip bir yöneticisi tarafından tek tıkla onaylanır — ayrı bir Azure uygulaması oluşturmanıza gerek yoktur.</>,
    en: () => <>To distribute signatures to your Outlook users automatically, connect your Microsoft 365 account. A <strong>Global Admin</strong> in your organization approves it with one click — no separate Azure app registration needed.</>,
  },
  redirecting: { tr: 'Yönlendiriliyor...', en: 'Redirecting...' },
  connect: { tr: 'Microsoft 365\'e Bağlan', en: 'Connect Microsoft 365' },
};

export const GraphConnectionCard = ({ resultParam, disabledReason }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState('');
  const { t, formatDate } = useT(messages);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get(endpoints.organizations.graphStatus);
      setStatus(res);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  useEffect(() => {
    if (!resultParam) return;
    if (resultParam.connected === '1') {
      setError('');
    } else if (resultParam.connected === '0') {
      setError(t(FAILURE_REASONS.includes(resultParam.reason) ? resultParam.reason : 'connectFailed'));
    }
  }, [resultParam, t]);

  const handleConnect = async () => {
    setConnecting(true);
    setError('');
    try {
      const res = await api.get(endpoints.organizations.graphConsentUrl);
      window.location.href = res.consent_url;
    } catch (err) {
      setError(apiErrorMessage(err, messages.startFailed));
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(t('confirmDisconnect'))) return;
    setDisconnecting(true);
    try {
      await api.post(endpoints.organizations.graphDisconnect);
      await fetchStatus();
    } catch {
      setError(t('disconnectFailed'));
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <Card className="graph-connection-card">
        <Spinner size="sm" />
      </Card>
    );
  }

  const connected = status?.connected;

  if (!connected && disabledReason) {
    return (
      <Card className="graph-connection-card not-connected">
        <div className="graph-connection-header">
          <div className="graph-connection-title">
            <span className="graph-connection-icon">
              <svg width="22" height="22" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="11" height="11" fill="#f25022" />
                <rect x="12" width="11" height="11" fill="#7fba00" />
                <rect y="12" width="11" height="11" fill="#00a4ef" />
                <rect x="12" y="12" width="11" height="11" fill="#ffb900" />
              </svg>
            </span>
            <h3>{t('title')}</h3>
          </div>
          <Badge variant="warning">{t('notConnected')}</Badge>
        </div>
        <p className="graph-connection-desc">{disabledReason}</p>
      </Card>
    );
  }

  return (
    <Card className={`graph-connection-card ${connected ? 'connected' : 'not-connected'}`}>
      <div className="graph-connection-header">
        <div className="graph-connection-title">
          <span className="graph-connection-icon">
            <svg width="22" height="22" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="11" height="11" fill="#f25022" />
              <rect x="12" width="11" height="11" fill="#7fba00" />
              <rect y="12" width="11" height="11" fill="#00a4ef" />
              <rect x="12" y="12" width="11" height="11" fill="#ffb900" />
            </svg>
          </span>
          <h3>{t('title')}</h3>
        </div>
        {connected ? (
          <Badge variant="success">{t('connected')}</Badge>
        ) : (
          <Badge variant="warning">{t('notConnected')}</Badge>
        )}
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {connected ? (
        <>
          <p className="graph-connection-desc">
            {t('connectedDesc', { tenant: status.tenant_name || t('yourTenant'), deployed: status.addin_deployed })}
          </p>
          {status.connected_at && (
            <p className="graph-connection-meta">{t('connectedAt', { date: formatDate(status.connected_at, { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) })}</p>
          )}
          <Button variant="secondary" onClick={handleDisconnect} disabled={disconnecting}>
            {disconnecting ? t('removing') : t('disconnect')}
          </Button>
        </>
      ) : (
        <>
          <p className="graph-connection-desc">{t('notConnectedDesc')}</p>
          <Button onClick={handleConnect} disabled={connecting}>
            {connecting ? t('redirecting') : t('connect')}
          </Button>
        </>
      )}
    </Card>
  );
};

export default GraphConnectionCard;
